import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export interface FocusRailItem {
  id: number;
  title: string;
  description: string;
  meta?: string;
  imageSrc: string;
  href: string;
}

interface FocusRailProps {
  items: FocusRailItem[];
  autoplay?: boolean;
  interval?: number;
  className?: string;
}

function SmartLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

export function FocusRail({
  items,
  autoplay = true,
  interval = 8000,
  className,
}: FocusRailProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!autoplay || isPaused || prefersReducedMotion) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [autoplay, isPaused, interval, next, prefersReducedMotion]);

  const activeItem = items[activeIndex];

  // Image fade transition (no movement, just opacity)
  const imageTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.35, ease: "easeOut" as const };

  // Text crossfade — opacity only, no Y translate, so the reader's eye stays still
  const textTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.25, ease: "easeOut" as const };

  return (
    <div
      className={cn("w-full", className)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
    >
      {/* Desktop layout */}
      <div className="hidden md:grid md:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
        {/* Left: item list */}
        <div className="flex flex-col gap-1">
          {items.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setActiveIndex(index)}
              className={cn(
                "group relative w-full text-left px-5 py-4 rounded-xl transition-all duration-300",
                index === activeIndex
                  ? "bg-primary/10 shadow-sm"
                  : "hover:bg-muted/60"
              )}
            >
              {/* Active indicator bar */}
              {index === activeIndex && (
                <motion.div
                  layoutId="focus-rail-indicator"
                  className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}

              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "font-medium text-sm transition-colors",
                    index === activeIndex
                      ? "text-primary"
                      : "text-muted-foreground group-hover:text-foreground"
                  )}
                >
                  {item.title}
                </span>
                <ChevronRight
                  className={cn(
                    "h-4 w-4 transition-all",
                    index === activeIndex
                      ? "text-primary opacity-100 translate-x-0"
                      : "opacity-0 -translate-x-2"
                  )}
                />
              </div>

              {index === activeIndex && item.meta && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="text-xs text-muted-foreground mt-1"
                >
                  {item.meta}
                </motion.p>
              )}
            </button>
          ))}

          {/* Progress dots */}
          <div className="flex gap-1.5 mt-4 px-5">
            {items.map((item, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                aria-label={`Ver ${item.title}`}
                aria-current={i === activeIndex}
                className="p-2 -m-2 inline-flex items-center justify-center bg-transparent"
              >
                <span
                  className={cn(
                    "block h-1.5 rounded-full transition-all duration-300",
                    i === activeIndex
                      ? "w-6 bg-primary"
                      : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  )}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Right: image preview */}
        <div className="relative">
          <SmartLink href={activeItem.href}>
            {/* Browser frame — static */}
            <div className="rounded-2xl border border-border/40 bg-card/80 backdrop-blur-sm shadow-[0_20px_70px_-15px_rgba(0,0,0,0.12)] overflow-hidden ring-1 ring-black/[0.03]">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-border/30 bg-muted/40">
                <div className="size-3 rounded-full bg-red-400/80" />
                <div className="size-3 rounded-full bg-yellow-400/80" />
                <div className="size-3 rounded-full bg-green-400/80" />
                <div className="ml-4 flex-1 h-6 rounded-md bg-muted/60 max-w-xs" />
              </div>
              {/* Image area: only the image crossfades, frame stays put */}
              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeItem.id}
                    src={activeItem.imageSrc}
                    alt={activeItem.title}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={imageTransition}
                    className="w-full h-auto block"
                    loading="lazy"
                    decoding="async"
                  />
                </AnimatePresence>
              </div>
            </div>
          </SmartLink>

          {/* Description block — text stays in place, only content crossfades */}
          <div className="mt-5 px-1 min-h-[5.5rem]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeItem.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={textTransition}
              >
                <h3 className="text-lg font-semibold text-foreground">
                  {activeItem.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-lg">
                  {activeItem.description}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Background glow */}
          <div className="absolute -inset-10 -z-10 bg-primary/5 rounded-3xl blur-3xl pointer-events-none" />
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden flex flex-col gap-6">
        {/* Mobile tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {items.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setActiveIndex(index)}
              className={cn(
                "shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all",
                index === activeIndex
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "bg-muted/60 text-muted-foreground"
              )}
            >
              {item.title.split(" ")[0]}
            </button>
          ))}
        </div>

        {/* Mobile image — frame static, image crossfades */}
        <div className="rounded-xl border border-border/40 bg-card/80 shadow-lg overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border/30 bg-muted/40">
            <div className="size-2 rounded-full bg-red-400/80" />
            <div className="size-2 rounded-full bg-yellow-400/80" />
            <div className="size-2 rounded-full bg-green-400/80" />
          </div>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeItem.id}
                src={activeItem.imageSrc}
                alt={activeItem.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={imageTransition}
                className="w-full h-auto block"
                loading="lazy"
                decoding="async"
              />
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile description — static container, text crossfades */}
        <div className="min-h-[7rem]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeItem.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={textTransition}
            >
              <h3 className="text-base font-semibold text-foreground">
                {activeItem.title}
              </h3>
              {activeItem.meta && (
                <p className="text-xs text-primary/70 mt-0.5">{activeItem.meta}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {activeItem.description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
